import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import JourneyHeader from "@/components/JourneyHeader";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, Home} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// inProgress: true → shows spinner/"In Progress" when visible
// inProgress: false → shows checkmark/"Done" when visible
const steps: { label: string; inProgress: boolean }[] = [
  { label: "Shortlisting offers from top lenders", inProgress: false },
  { label: "Generating your business profile", inProgress: true },
  { label: "Personalising your loan offers", inProgress: true },
];

type StepStatus = "pending" | "completed" | "in-progress";

const CuratingOffersWhitespace = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [visibleSteps, setVisibleSteps] = useState<number>(0);

  const handleExit = async () => {
    await logout();
    navigate('/');
  };

  useEffect(() => {
    // Reveal steps one by one with a staggered delay
    const timers: ReturnType<typeof setTimeout>[] = [];
    steps.forEach((_, i) => {
      timers.push(
        setTimeout(() => {
          setVisibleSteps((prev) => Math.max(prev, i + 1));
        }, 600 + i * 800)
      );
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  const getStepStatus = (index: number): StepStatus => {
    if (visibleSteps <= index) return "pending";
    return steps[index].inProgress ? "in-progress" : "completed";
  };

  return (
    <div className="min-h-screen bg-intellend-white flex flex-col">
      <JourneyHeader />

      <div className="flex-1 flex flex-col items-center px-4 py-10">
        {/* Headline — at the top */}
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 text-center mb-3 mt-2">
          We're Curating Offers for You
        </h1>
        <p className="text-gray-500 text-center max-w-sm mb-10 text-base leading-relaxed">
          Intellend is working behind the scenes to find the most suitable loan
          offers tailored to your profile.
        </p>

        {/* Step indicators */}
        <div className="w-full max-w-sm space-y-3 mb-10">
          {steps.map((step, i) => {
            const status = getStepStatus(i);
            return (
              <div
                key={i}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-500 ${
                  status === "completed"
                    ? "bg-intellend-green/5 border-intellend-green/20 opacity-100 translate-y-0"
                    : status === "in-progress"
                    ? "bg-blue-50 border-blue-200 opacity-100 translate-y-0"
                    : "bg-gray-50 border-gray-100 opacity-0 translate-y-2"
                }`}
              >
                {status === "completed" ? (
                  <CheckCircle2 className="w-5 h-5 text-intellend-green flex-shrink-0" />
                ) : status === "in-progress" ? (
                  <div className="w-5 h-5 rounded-full border-2 border-blue-200 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                )}
                <span
                  className={`text-sm font-medium ${
                    status === "completed"
                      ? "text-green-800"
                      : status === "in-progress"
                      ? "text-blue-700"
                      : "text-gray-400"
                  }`}
                >
                  {step.label}
                </span>
                {status === "in-progress" && (
                  <span className="ml-auto text-xs font-semibold text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full">
                    In Progress
                  </span>
                )}
                {status === "completed" && (
                  <span className="ml-auto text-xs font-semibold text-white bg-intellend-green px-2 py-0.5 rounded-full">
                    Done
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* What happens next — prominent card */}
        <div className="w-full max-w-sm bg-intellend-green text-white rounded-2xl p-6 shadow-lg flex flex-col gap-3">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-7 h-7 text-white flex-shrink-0" />
            <p className="text-2xl font-bold tracking-wide">What happens next?</p>
          </div>
          <p className="text-base leading-relaxed text-white/90">
            Our team at <span className="font-bold text-white">Intellend</span> will
            review your details and reach out to you with personalised loan offers
            within <span className="font-bold text-white">24–48 hours</span>.
          </p>
          <p className="text-base text-white/80 font-medium">
            📱 Please keep your phone handy.
          </p>
        </div>

        {/* Exit to Home button */}
        <div className="flex justify-center mt-6 w-full max-w-sm">
          <Button
            onClick={handleExit}
            className="flex items-center gap-2 w-full md:w-auto"
            variant="default"
          >
            <Home size={18} />
            Exit to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CuratingOffersWhitespace;
